import React, { useEffect, useMemo, useState } from 'react';

function formatActuals(value) {
    if (value === '' || value == null) return '';
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    return Number.isInteger(n) ? n.toFixed(0) : n.toFixed(2);
}

export default function KPIReviewRowManager({
                                                kpi,
                                                review,
                                                ratings = [],
                                                onSave,
                                                counterText = ''
                                            }) {
    const ratingById = useMemo(() => {
        const m = new Map();
        ratings.forEach(r => m.set(String(r.id), r));
        return m;
    }, [ratings]);

    const employeeRatingId = review?.employee_rating_id ?? '';
    const employeeRatingCode = useMemo(() => {
        if (!employeeRatingId) return '';
        const r = ratingById.get(String(employeeRatingId));
        return r?.code ?? '';
    }, [employeeRatingId, ratingById]);

    const [managerRatingId, setManagerRatingId] = useState(review?.manager_rating_id ?? '');
    const managerRatingCode = useMemo(() => {
        if (!managerRatingId) return '';
        const r = ratingById.get(String(managerRatingId));
        return r?.code ?? '';
    }, [managerRatingId, ratingById]);

    const [saving, setSaving] = useState(false);

    const onSubmit = async (nextManagerRatingId = managerRatingId) => {
        if (!onSave || saving) return;
        const payload = {
            manager_rating_id: nextManagerRatingId || null,
            manager_description: nextManagerRatingId ? (ratingById.get(String(nextManagerRatingId))?.code ?? null) : null,
        };
        try {
            setSaving(true);
            await onSave({ reviewId: review?.id, kpiId: kpi.id, payload });
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        setManagerRatingId(review?.manager_rating_id ?? '');
    }, [review?.manager_rating_id]);

    const handleManagerRatingChange = (e) => {
        const nextId = e.target.value ? Number(e.target.value) : '';
        setManagerRatingId(nextId);
        onSubmit(nextId);
    };

    return (
        <tr>
            <td className="rt-counter">{counterText}</td>
            <td className="rt-name">{kpi.name}</td>

            <td className="rt-num">
                {kpi.is_percentage ? `${kpi.threshold ?? '—'}%` : (kpi.threshold ?? '—')}
                {!kpi.is_percentage && kpi.unit ? ` ${kpi.unit}` : ''}
            </td>
            <td className="rt-num">
                {kpi.is_percentage ? `${kpi.target ?? '—'}%` : (kpi.target ?? '—')}
                {!kpi.is_percentage && kpi.unit ? ` ${kpi.unit}` : ''}
            </td>
            <td className="rt-num">
                {kpi.is_percentage ? `${kpi.stretch ?? '—'}%` : (kpi.stretch ?? '—')}
                {!kpi.is_percentage && kpi.unit ? ` ${kpi.unit}` : ''}
            </td>

            <td className="rt-num">{kpi.weight ?? '—'}</td>

            {/* Actuals (read-only) */}
            <td style={{ minWidth: 110 }}>
                <input
                    type="text"
                    className="rt-input"
                    value={formatActuals(review?.actuals ?? '')}
                    readOnly
                    disabled
                />
            </td>

            {/* Employee Rating (read-only ID) */}
            <td style={{ minWidth: 110 }}>
                <input
                    className="rt-input"
                    value={employeeRatingId || ''}
                    readOnly
                    disabled
                    placeholder="—"
                />
            </td>

            {/* Employee Description (auto code) */}
            <td style={{ minWidth: 140 }}>
                <input
                    className="rt-input"
                    value={employeeRatingCode}
                    readOnly
                    disabled
                    placeholder="Description"
                />
            </td>

            {/* Manager Rating (editable select showing IDs) */}
            <td style={{ minWidth: 110 }}>
                <select
                    className="rt-select"
                    value={managerRatingId || ''}
                    onChange={handleManagerRatingChange}
                >
                    <option value="">--</option>
                    {ratings.map(r => (
                        <option key={r.id} value={r.id}>
                            {r.id}
                        </option>
                    ))}
                </select>
            </td>

            {/* Manager Description (auto code) */}
            <td style={{ minWidth: 140 }}>
                <input
                    className="rt-input"
                    value={managerRatingCode}
                    readOnly
                    disabled
                    placeholder="Description"
                />
            </td>

            <td className="rt-actions" style={{ minWidth: 110 }}>
                {/* Auto-save on selection; no manual button needed */}
            </td>
        </tr>
    );
}
